/**
 * GameLayout - Game session screen layout
 *
 * Connection lifecycle is handled by GameConnectionManager.
 * This component just renders based on connectionStatus.
 *
 * Layout:
 * - Left column: Scene (with chars + items sections) → Tactical Map
 * - Middle: Narrative Panel (full height, main chat interface)
 * - Right column: Character Stats (top) → Party Status (bottom)
 */

import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { cn } from '@/lib/utils';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import ScenePanel from '@/components/panels/ScenePanel';
import TacticalMapPanel from '@/components/panels/TacticalMapPanel';
import CharDetailsPanel from '@/components/panels/CharDetailsPanel';
import ChatPanel from '@/components/panels/ChatPanel';
import MapPanel from '@/components/panels/MapPanel';
import PartyStatusPanel from '@/components/panels/PartyStatusPanel';
import GameBackground from '@/components/game/GameBackground';
import PromoBanner from '@/components/promo/PromoBanner';
import { WORLD3D_ENABLED } from '@/world/config';
import { ReconnectScreen } from '@/components/common/ReconnectScreen';
import { DisconnectedOverlay } from '@/components/common/DisconnectedOverlay';
import { gameConnectionManager } from '@/lib/game-connection-manager';
import { useGameStore } from '@/lib/stores/gameStore';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { clearGameSessionState } from '@/lib/clear-game-session-state';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { usePlayerActivityStore } from '@/lib/stores/playerActivityStore';
import { toast } from '@/lib/toast';
import { sessionEndingBannerText } from '@/lib/session-end-reason';
import { apiClient } from '@/lib/api';
import { nextPmBalanceFetchSeq, usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { track } from '@/lib/analytics';
import { DISCORD_INVITE_URL } from '@/lib/constants';

// Dead-branch dynamic import, the same pattern the dev-only viewer routes use
// in `routes.tsx`. `WORLD3D_ENABLED` is a compile-time constant, so while it is
// false the bundler drops WorldCanvas and the entire three.js stack from the
// production bundle. A static import would ship three.js as dead weight even
// though `worldActive` never turns true.
const WorldCanvas = WORLD3D_ENABLED ? lazy(() => import('@/components/game/WorldCanvas')) : null;

// `> 0` instead of `||` so VITE_…=0 doesn't silently fall back to the default
// (0 is falsy → || would skip it). Negative/NaN also falls through to default.
// Keep below the server's DEFAULT_IDLE_TIMEOUT_SECS (15 min) so the client
// ends the session first and the server sweep stays a backstop.
const GAME_IDLE_TIMEOUT_MS =
  Number(import.meta.env.VITE_GAME_IDLE_TIMEOUT_MS) > 0
    ? Number(import.meta.env.VITE_GAME_IDLE_TIMEOUT_MS)
    : 10 * 60 * 1000;

const LOCAL_STORAGE_LAYOUT_KEY = 'game-layout';

// Flip to `true` to re-enable user column resizing.
// While false: handles are non-interactive and visually hidden, columns lock to
// `defaultLayout`, persisted layouts in localStorage are ignored. Code path is
// preserved end-to-end (PanelGroup/Panel/PanelResizeHandle + onLayout + storage).
const COLUMN_RESIZE_ENABLED = false;

/** Fires a persistent sonner toast when session is ending (bottom-right, matches connection toasts) */
function SessionEndingToast({ reason }: { reason?: string }) {
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    toast.loading(sessionEndingBannerText(reason), {
      duration: Infinity,
      id: 'session-ending',
    });
  }, [reason]);
  return null;
}

export default function GameLayout() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { connectionStatus, partyName, moduleTitle, gameInitialized, currentScene } =
    useGameStore();

  const [layout, setLayout] = useState<number[] | undefined>(() => {
    if (!COLUMN_RESIZE_ENABLED) return undefined;
    try {
      const storedLayout = localStorage.getItem(LOCAL_STORAGE_LAYOUT_KEY);
      return storedLayout ? JSON.parse(storedLayout) : undefined;
    } catch (error) {
      console.error('Failed to parse stored layout from localStorage', error);
      return undefined;
    }
  });

  const defaultLayout = useMemo(() => [25, 50, 25], []);

  // Derive game server URLs once on mount — useMemo([]) prevents useEffect
  // re-fires when localStorage is cleared during session end.
  const gameServerWsUrl = useMemo(() => {
    const stored = localStorage.getItem('game-session-ws-url');
    if (stored) return stored;
    if (import.meta.env.VITE_GAME_WS_URL) return import.meta.env.VITE_GAME_WS_URL;
    const envHttpUrl = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3000';
    const wsBase = envHttpUrl.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
    return `${wsBase}/ws`;
  }, []);
  const gameServerHttpUrl = useMemo(() => {
    const stored = localStorage.getItem('game-session-ws-url');
    if (stored) {
      const httpUrl = stored.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
      return httpUrl.replace(/\/ws\/[^/]+$/, '').replace(/\/ws$/, '');
    }
    return import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3000';
  }, []);

  // Track whether this session was freshly created by the lobby (vs. stale bookmark/refresh).
  // Used by the ended-effect to decide whether to set the loop-breaker flag.
  const freshFromLobbyRef = useRef(false);

  // Hydrate the in-chat low-balance gate's store on entry to /play. The gate
  // (ChatPanel sends + scene-panel taps) treats a null cache as "allowed" to
  // avoid a cold-start flash, and `lobby:balance-update` only arrives over the
  // lobby WS — which a direct /play deep-link or hard reload never connects, so
  // the cache stays null and the first metered action bypasses the gate.
  // One-shot REST fetch, mirroring DashboardPage.
  useEffect(() => {
    const seq = nextPmBalanceFetchSeq();
    apiClient
      .getPmBalance()
      .then(({ pmTokenBalance }) => usePmBalanceStore.getState().setPmBalance(pmTokenBalance, seq))
      .catch(() => {});
  }, []);

  // Single effect: start/end session lifecycle
  useEffect(() => {
    if (!sessionId) {
      navigate('/lobby', { replace: true });
      return;
    }

    // Bail out if this /play mount is a stale-redirect to a session that the
    // FE already cleared. After /end-session, `clearGameSessionState` removes
    // `active-game-session` from localStorage. If the URL's sessionId no
    // longer matches what localStorage tracks AND a session ended in the
    // last 30s, ProtectedRoute is racing the cleanup and bouncing us back
    // to /play for the just-ended session. Don't startSession — navigate to
    // /lobby and let the fast-path settle. Critically: a FRESH
    // /start-adventure within 30s of a prior end-session writes a NEW
    // sessionId to localStorage BEFORE this mount, so the match check
    // permits the legitimate next session.
    const justEnded = sessionStorage.getItem('session-just-ended');
    if (justEnded) {
      const elapsed = Date.now() - parseInt(justEnded, 10);
      const trackedSessionId = localStorage.getItem('active-game-session');
      if (elapsed < 30_000 && trackedSessionId !== sessionId) {
        navigate('/lobby', { replace: true });
        return;
      }
    }

    // Bail out if `session_ended` already arrived for this session. The status-
    // watch effect below is about to navigate to /lobby; calling startSession
    // here would just hit the `sessionTerminated` reject path and produce a
    // noisy `[GameLayout] startSession rejected` warning. Read via getState()
    // so connectionStatus stays out of the deps array.
    if (useGameStore.getState().connectionStatus.status === 'ended') {
      return;
    }

    // Skip session validation for fresh sessions from lobby — it was just created.
    // sessionStorage flag set by LobbyLayout; consumed once to prevent stale skip on refresh.
    const isFreshFromLobby = sessionStorage.getItem('game-session-fresh') !== null;
    if (isFreshFromLobby) sessionStorage.removeItem('game-session-fresh');
    freshFromLobbyRef.current = isFreshFromLobby;

    gameConnectionManager
      .startSession(
        sessionId,
        gameServerWsUrl,
        gameServerHttpUrl,
        isFreshFromLobby ? { skipValidation: true } : undefined
      )
      .catch((error) => {
        console.warn('[GameLayout] startSession rejected:', error.message);
        // Defensive escape from spinner-forever: most reject paths transition
        // status to 'ended' / 'disconnected' / 'reconnecting' before rejecting
        // (status-watch effect or render switch handles each). The exception is
        // a synchronous WebSocket constructor throw, where status stays at
        // 'connecting' and the user sits on the ReconnectScreen indefinitely.
        // If status hasn't moved out of the pre-connection set, navigate.
        const currentStatus = useGameStore.getState().connectionStatus.status;
        if (
          currentStatus === 'idle' ||
          currentStatus === 'validating' ||
          currentStatus === 'connecting'
        ) {
          toast.error('Session unavailable', { description: 'Returning to lobby...' });
          navigate('/lobby', { replace: true });
        }
      });

    return () => {
      gameConnectionManager.endSession();
    };
  }, [sessionId, gameServerWsUrl, gameServerHttpUrl, navigate]);

  // Redirect on session end (guard with ref to prevent double-navigate)
  const endingRef = useRef(false);
  useEffect(() => {
    if (connectionStatus.status !== 'ended') {
      endingRef.current = false;
      return;
    }
    if (endingRef.current) return;
    endingRef.current = true;

    const reason = connectionStatus.reason;

    // Signal lobby that game start failed — prevents lobby→game→lobby loop.
    // Only set for fresh-from-lobby sessions to avoid false positives from
    // stale bookmarks or NOT_PARTICIPANT close codes.
    if (reason === 'error' && freshFromLobbyRef.current) {
      sessionStorage.setItem('game-start-failed', Date.now().toString());
    }

    const isInsufficientTokens = reason === 'insufficient_tokens';
    const isLeaderDisconnect = reason === 'leader_disconnect' || reason === 'all_players_gone';
    // Guests can't invite or buy (they'd bounce off /buy-tokens to /dashboard),
    // so steer them to the Discord-feedback bonus-tokens path. Signed-up users
    // lead with Invite friends (highest-yield earn path) and keep Buy Tokens as
    // the toast's secondary action.
    const isGuest = useAuthStore.getState().user?.isAnonymous ?? false;
    toast.info(
      isInsufficientTokens
        ? 'Out of Polymyth Tokens'
        : isLeaderDisconnect
          ? 'Session Ended'
          : 'Game Session Ended',
      {
        description: isInsufficientTokens
          ? isGuest
            ? 'Your session ended. Top up to keep playing, or join our Discord and share feedback for bonus tokens.'
            : 'Your session ended. Invite friends to play together for the biggest token boost, or top up to keep playing.'
          : isLeaderDisconnect
            ? 'Connection lost. Your progress has been saved.'
            : 'Returning to lobby...',
        duration: isInsufficientTokens ? 8000 : 5000,
        action: isInsufficientTokens
          ? isGuest
            ? {
                label: 'Get bonus tokens',
                onClick: () => {
                  track('discord_feedback_cta_click', {
                    surface: 'session_end_toast',
                    low_balance: true,
                  });
                  window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer');
                },
              }
            : {
                label: 'Invite friends',
                onClick: () => {
                  track('invite_cta_click', { surface: 'session_end_toast', low_balance: true });
                  useAccountModalStore.getState().openModal('invite');
                },
              }
          : undefined,
        cancel:
          isInsufficientTokens && !isGuest
            ? { label: 'Buy Tokens', onClick: () => navigate('/buy-tokens') }
            : undefined,
      }
    );
    clearGameSessionState('route-hints');
    // Dismiss the infinite session-ending toast before navigating
    toast.dismiss('session-ending');
    // Always land in the lobby on a clean session end — lobby is free
    // under the new policy and is where the in-chat low-balance gate +
    // BuyTokens flow live. Dashboard is no longer a recovery surface.
    navigate('/lobby');
  }, [connectionStatus, navigate]);

  const handleLayout = (sizes: number[]) => {
    if (!COLUMN_RESIZE_ENABLED) return;
    setLayout(sizes);
    try {
      localStorage.setItem(LOCAL_STORAGE_LAYOUT_KEY, JSON.stringify(sizes));
    } catch (error) {
      console.error('Failed to save layout to localStorage', error);
    }
  };

  // Soft-stuck escape: if a loading state lingers for 30s, show
  // DisconnectedOverlay-shaped UI with Retry + Return-to-lobby
  // ("every loading state has an escape").
  //
  // Covers TWO loading windows:
  //   1. validating/connecting — WS hasn't opened yet (session check, handshake)
  //   2. connected + !gameInitialized — WS is open but game_state hasn't
  //      arrived. This is the "Portaling to <module>..." screen during the
  //      backend's session_construct (cold Gemini agents = ~50s on a fresh
  //      Cloud Run instance). Without this arm, the user sees the spinner
  //      indefinitely while the WS sits idle waiting for game_state — no
  //      reload-prompt, no recovery path. Reload was the only escape.
  const [softStuck, setSoftStuck] = useState(false);
  useEffect(() => {
    const status = connectionStatus.status;
    const isPreConnect = status === 'validating' || status === 'connecting';
    const isWaitingForGameState = status === 'connected' && !gameInitialized;
    if (!isPreConnect && !isWaitingForGameState) {
      setSoftStuck(false);
      return;
    }
    const timer = setTimeout(() => setSoftStuck(true), 30_000);
    return () => clearTimeout(timer);
  }, [connectionStatus.status, gameInitialized]);

  const handleSoftStuckRetry = useCallback(async () => {
    if (!sessionId) return;
    setSoftStuck(false);
    try {
      await gameConnectionManager.startSession(sessionId, gameServerWsUrl, gameServerHttpUrl);
    } catch {
      setSoftStuck(true);
    }
  }, [sessionId, gameServerWsUrl, gameServerHttpUrl]);

  const handleSoftStuckReturnToLobby = useCallback(() => {
    gameConnectionManager.endSession();
    clearGameSessionState('route-hints');
    navigate('/lobby');
  }, [navigate]);

  // Game idle: after GAME_IDLE_TIMEOUT_MS with no player intent, end the
  // backend session and return to /dashboard. We send `end_session` over the
  // existing authenticated WS so the server runs its canonical
  // `handle_session_end` path — scene snapshot, Firestore cleanup, recap,
  // lobby notify. Without ending server-side, Gemini keeps producing rounds
  // against an empty session and burns the player's PM tokens.
  //
  // "Player intent" = an outbound WS message via gameConnectionManager.sendAction
  // (text submit, card click, slash command, target picker). NPC narration is
  // a server-push so it never bumps the timer — which is the whole point of
  // moving off DOM events: NPCs auto-scrolled the chat panel and indefinitely
  // suppressed the AFK kick (#284 was the FE-side patch; this is the
  // signal-source replacement).
  //
  // Pause while validating/connecting/reconnecting so we don't bail out
  // before we've even finished joining.
  const lastPlayerIntentAt = usePlayerActivityStore((s) => s.lastPlayerIntentAt);
  // One-shot warn debounce: only toast once per "entered mid-combat idle"
  // window. The pause path re-fires the watcher every timeoutMs while the
  // PC stays upright in combat; without this guard the player would get
  // the same toast every idle interval.
  const idleCombatWarnedRef = useRef(false);
  const { resetTimer } = useInactivityTimeout({
    timeoutMs: GAME_IDLE_TIMEOUT_MS,
    // Default DOM activity events (mouse/keys/touch) reset the timer: a
    // player reading narration or watching a co-player's turn is PRESENT,
    // not idle. Intent submission below also resets (covers keyboard-less
    // flows). Verified live: with events [] the watchdog ended a
    // multiplayer session under an active player who hadn't submitted an
    // intent for 5 minutes.
    shouldPauseTimeout: () => {
      const status = connectionStatus.status;
      if (status === 'validating' || status === 'connecting' || status === 'reconnecting') {
        return true;
      }
      // Mirror the server-side mid-combat block in
      // `SessionMessage::ClientForceEnd`: if the PC is upright in combat,
      // the backend refuses the end and replies with a SystemNotification
      // while keeping the session live. We must not tear down the FE in
      // that case (the user would see "Session ended" while the backend
      // session lives and burns tokens until leader-disconnect cleanup).
      // Returning true here both suppresses the end AND re-arms the timer
      // (the hook's `fire()` re-schedules itself on the pause branch),
      // so the watcher resumes naturally once combat ends.
      const { encounterType, playerCharacterId, partyMembersMap } = useGameStore.getState();
      const myChar = playerCharacterId ? partyMembersMap[playerCharacterId] : null;
      const pcUprightInCombat =
        encounterType === 'combat' && myChar != null && (myChar.hp ?? 0) > 0;
      if (pcUprightInCombat) {
        if (!idleCombatWarnedRef.current) {
          idleCombatWarnedRef.current = true;
          toast.warning('You went idle mid-combat. Finish the encounter or step away to end.');
        }
        return true;
      }
      idleCombatWarnedRef.current = false;
      return false;
    },
    onTimeout: () => {
      gameConnectionManager.sendAction({ type: 'end_session', reason: 'fe-idle-timeout' });
      // Brief grace so the server processes the message before our local
      // tear-down closes the socket. The shutdown path is async server-side
      // anyway; we do not block the redirect on its completion.
      setTimeout(() => {
        gameConnectionManager.endSession();
        clearGameSessionState('all');
        toast.info('You were idle. Session ended — your progress was saved.');
        navigate('/dashboard');
      }, 250);
    },
  });
  useEffect(() => {
    resetTimer();
  }, [lastPlayerIntentAt, resetTimer]);

  const isTrainingSession = pathname.includes('party_training_');
  // 3D combat world: active whenever the current scene ships a tactical map.
  // GameBackground is suppressed while the world renders EXCEPT in training
  // sessions (it serves the duel arena backdrop there).
  const worldActive = WORLD3D_ENABLED && Boolean(currentScene?.tacticalMap);
  const moduleName =
    moduleTitle ||
    localStorage.getItem('game-session-module') ||
    (isTrainingSession ? 'Training Grounds' : 'the adventure');
  const displayPartyName =
    partyName ||
    (() => {
      try {
        const stored = localStorage.getItem('game-session-party');
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.name;
        }
      } catch {
        // Ignore parse errors
      }
      return null;
    })();

  // Lobby store is the SOT for live portrait URLs. Subscribing here lets the
  // duel loader stay in sync with whatever URL the lobby UI is currently
  // showing — even if the localStorage snapshot was stale or the server's
  // session.portrait_url lookup came back null.
  const lobbyCharacters = useLobbyStore((s) => s.lobbyCharacters);
  const lobbyOwnCharacters = useLobbyStore((s) => s.characters);

  // Parse duel participant portraits and resolve URLs from the live lobby
  // store when we have charIds. Falls back to whatever URL was persisted at
  // /duel-click time, then to the server's lobby:game-started URL.
  const duelParticipants = useMemo(() => {
    if (!isTrainingSession) return undefined;
    let stored: {
      player?: { charId?: string; name?: string; imageUrl?: string };
      opponent?: { charId?: string; name?: string; imageUrl?: string };
    } | null = null;
    try {
      const raw = localStorage.getItem('game-session-duel-portraits');
      if (raw) stored = JSON.parse(raw);
    } catch {
      // Ignore parse errors
    }
    if (!stored) return undefined;

    const resolveUrl = (charId: string | undefined, fallback: string | undefined) => {
      if (!charId) return fallback;
      const own = lobbyOwnCharacters.find((c) => c.id === charId);
      const lobby = lobbyCharacters.find((c) => c.id === charId);
      return (
        own?.portraitUrl ??
        own?.session?.portrait_url ??
        own?.avatarUrl ??
        lobby?.portraitUrl ??
        lobby?.avatarUrl ??
        fallback
      );
    };

    const resolved = {
      player: {
        name: stored.player?.name,
        imageUrl: resolveUrl(stored.player?.charId, stored.player?.imageUrl),
      },
      opponent: {
        name: stored.opponent?.name,
        imageUrl: resolveUrl(stored.opponent?.charId, stored.opponent?.imageUrl),
      },
    };
    console.log('[duel-loader] GameLayout resolved duelParticipants', {
      stored,
      resolved,
      lobbyOwnCount: lobbyOwnCharacters.length,
      lobbyCharsCount: lobbyCharacters.length,
    });
    return resolved;
  }, [isTrainingSession, lobbyCharacters, lobbyOwnCharacters]);

  // Render based on connection status
  switch (connectionStatus.status) {
    case 'idle':
    case 'validating':
    case 'connecting': {
      return (
        <>
          <ReconnectScreen
            adventureName={moduleName}
            partyName={displayPartyName || undefined}
            heroImageUrl={localStorage.getItem('game-session-hero-image') || undefined}
            heroVideoUrl={localStorage.getItem('game-session-hero-video') || undefined}
            duelParticipants={duelParticipants}
            message={
              isTrainingSession
                ? 'Entering the Training Grounds...'
                : 'Opening a portal to your adventure...'
            }
          />
          {softStuck && (
            <DisconnectedOverlay
              onReconnect={handleSoftStuckRetry}
              onReturnToLobby={handleSoftStuckReturnToLobby}
            />
          )}
        </>
      );
    }

    case 'reconnecting':
      return (
        <ReconnectScreen
          adventureName={moduleName}
          partyName={displayPartyName || undefined}
          heroImageUrl={localStorage.getItem('game-session-hero-image') || undefined}
          heroVideoUrl={localStorage.getItem('game-session-hero-video') || undefined}
          duelParticipants={duelParticipants}
          message="Reconnecting to your adventure..."
        />
      );

    case 'disconnected':
      return (
        <DisconnectedOverlay
          onReconnect={() => gameConnectionManager.manualReconnect()}
          onReturnToLobby={() => {
            gameConnectionManager.endSession();
            navigate('/lobby');
          }}
        />
      );

    case 'ended':
      return (
        <div className="flex-1 h-screen flex items-center justify-center">
          <AnkhSpinner size="lg" className="text-accent-gold" />
        </div>
      );

    case 'ending':
    case 'connected': {
      if (!gameInitialized) {
        return (
          <ReconnectScreen
            adventureName={moduleName}
            partyName={displayPartyName || undefined}
            heroImageUrl={localStorage.getItem('game-session-hero-image') || undefined}
            heroVideoUrl={localStorage.getItem('game-session-hero-video') || undefined}
            duelParticipants={duelParticipants}
            message={
              isTrainingSession
                ? 'Entering the Training Grounds...'
                : `Portaling to ${moduleName || 'your adventure'}...`
            }
          />
        );
      }
      return (
        <div className="flex-1 p-4 h-screen relative overflow-hidden min-w-[1024px]">
          {(!worldActive || isTrainingSession) && <GameBackground />}
          {connectionStatus.status === 'ending' && (
            <SessionEndingToast reason={connectionStatus.reason} />
          )}
          <PanelGroup
            direction="horizontal"
            className="h-full relative z-10"
            onLayout={handleLayout}
          >
            {/* Left Column: Scene (with chars + items sections) → Tactical Map */}
            {/* During training duels: ScenePanel shows split portraits + HP tracking */}
            <Panel defaultSize={layout ? layout[0] : defaultLayout[0]} minSize={20}>
              <div className="h-full flex flex-col gap-2 overflow-y-auto">
                {/* shrink-0: this column scrolls, so the scene card keeps its
                    content height instead of being squeezed by its siblings. */}
                <ScenePanel context="game-session" className="shrink-0" />
                {/* Renders itself only during combat; outside an encounter the
                    engine has cleared every grid position anyway. */}
                <TacticalMapPanel />
                {!isTrainingSession && <MapPanel context="game-session" />}
              </div>
            </Panel>

            <PanelResizeHandle
              disabled={!COLUMN_RESIZE_ENABLED}
              className={cn(
                COLUMN_RESIZE_ENABLED
                  ? 'w-1 bg-transparent hover:bg-border-subtle transition-colors duration-150 mx-2 flex items-center justify-center after:h-16 after:w-1 after:bg-border after:rounded-full after:hover:bg-primary after:transition-colors after:duration-150'
                  : 'w-0 mx-2 pointer-events-none'
              )}
            />

            {/* Middle: the 3D world (hero-tall) above Chat when the scene has a
                tactical map and the world renderer is on. Otherwise Chat with the
                promo banner riding ON TOP of this column only, so it lowers the
                chat, not the side panels. */}
            <Panel defaultSize={layout ? layout[1] : defaultLayout[1]} minSize={30}>
              {worldActive && WorldCanvas ? (
                <div className="h-full flex flex-col gap-2 min-h-0">
                  <div className="h-[56%] min-h-0 shrink-0">
                    <Suspense fallback={null}>
                      <WorldCanvas />
                    </Suspense>
                  </div>
                  <div className="flex-1 min-h-0">
                    <ChatPanel />
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col">
                  <PromoBanner />
                  <div className="min-h-0 flex-1">
                    <ChatPanel />
                  </div>
                </div>
              )}
            </Panel>

            <PanelResizeHandle
              disabled={!COLUMN_RESIZE_ENABLED}
              className={cn(
                COLUMN_RESIZE_ENABLED
                  ? 'w-1 bg-transparent hover:bg-border-subtle transition-colors duration-150 mx-2 flex items-center justify-center after:h-16 after:w-1 after:bg-border after:rounded-full after:hover:bg-primary after:transition-colors after:duration-150'
                  : 'w-0 mx-2 pointer-events-none'
              )}
            />

            {/* Right Column: Party Status → Character Details */}
            <Panel defaultSize={layout ? layout[2] : defaultLayout[2]} minSize={20}>
              <div className="h-full flex flex-col gap-2 overflow-y-auto">
                {!isTrainingSession && <PartyStatusPanel />}
                <CharDetailsPanel context="game-session" />
              </div>
            </Panel>
          </PanelGroup>
        </div>
      );
    }
  }
}
